<template>
    <div class="packlist">
        <!-- Hidden Scanner Input for DataWedge (TC200 & TC22) - Desktop/Hardware scanners -->
        <!-- Only render when scanner mode is enabled to prevent interference with foot keyboard -->
        <input
            v-if="isDesktop && scannerEnabled"
            ref="scannerInput"
            type="text"
            inputmode="none"
            :class="scannerOptions.debug ? 'scanner-input-visible' : 'scanner-input-hidden'"
            @input="handleScannerInput"
            @keydown="handleScannerKeydownOverride"
            @focus="handleScannerFocus"
            @blur="handleScannerBlur"
            autocomplete="off"
        />

        <!-- Hidden Scanner Input for Tablets/Mobile - Optimized for camera/built-in scanners -->
        <!-- Only render when scanner mode is enabled to prevent interference with foot keyboard -->
        <input
            v-else-if="!isDesktop && scannerEnabled"
            ref="scannerInput"
            type="text"
            class="scanner-input-hidden"
            inputmode="none"
            readonly
            onfocus="this.removeAttribute('readonly');"
            @input="handleTabletScannerInput"
            @focus="handleScannerFocus"
            @blur="handleScannerBlur"
            autocomplete="off"
        />

        <!-- Dev Terminal (Desktop Only) -->
        <div v-if="!isMobile && isDevelopmentMode" class="dev-terminal">
            <div class="terminal-header">
                <div class="terminal-title">
                    <v-icon small color="#00ff41" class="mr-2">mdi-console</v-icon>
                    <span>PACKLIST_DEV</span>
                    <span class="terminal-blink ml-2">_</span>
                </div>
                <div class="terminal-status">
                    <span class="status-dot"></span>
                    <span>ONLINE</span>
                </div>
            </div>
            
            <div class="terminal-body">
                <div class="terminal-content">
                    <!-- Workflow State -->
                    <div class="terminal-section">
                        <div class="terminal-prompt">&gt; WORKFLOW_STATE</div>
                        <pre class="terminal-data">{{
                            {
                                needScan: needScan,
                                scannedItems: scannedItemsCount + '/' + totalItemsToScan,
                                canPrint: workflow.canPrint,
                                isPrinted: isPrinted,
                                canPack: workflow.canPack
                            }
                        }}</pre>
                    </div>

                    <!-- Scannable Items List -->
                    <div class="terminal-section" v-if="needScan && scannableItems && scannableItems.length > 0">
                        <div class="terminal-prompt">&gt; SCANNABLE_ITEMS ({{ scannableItems.length }})</div>
                        <div class="terminal-hint">Click to toggle scanned state</div>
                        <div class="terminal-article-list">
                            <div 
                                v-for="(article, index) in scannableItems" 
                                :key="index"
                                class="terminal-article-item"
                                :class="{ 'scanned': (article.scannedCount || 0) >= (article.qty || 1) }"
                                @click="devToggleArticleScanned(article)"
                            >
                                <div class="article-sku">{{ article.sku }}</div>
                                <div class="article-qty">{{ article.scannedCount || 0 }}/{{ article.qty || 1 }}</div>
                                <v-icon small :color="(article.scannedCount || 0) >= (article.qty || 1) ? '#00ff41' : '#666'">
                                    {{ (article.scannedCount || 0) >= (article.qty || 1) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                            </div>
                        </div>
                    </div>

                    <!-- Quick Actions -->
                    <div class="terminal-actions">
                        <button 
                            class="terminal-btn terminal-btn-success"
                            @click="devScanAllArticles"
                            :disabled="!needScan || allScannableItemsScanned"
                        >
                            <v-icon small class="mr-1">mdi-check-all</v-icon>
                            SCAN_ALL
                        </button>
                        <button 
                            class="terminal-btn terminal-btn-secondary"
                            @click="devResetScans"
                            :disabled="!needScan"
                        >
                            <v-icon small class="mr-1">mdi-refresh</v-icon>
                            RESET_SCANS
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Desktop Layout (L-shaped) - Used for ALL devices -->
        <div class="packlist-desktop-layout">
            <!-- Top section with sidebar, header column, and articles -->
            <div class="packlist-desktop-top">
                <!-- Desktop Header with PacklistItems -->
                <OrderHeader
                    v-if="currentOrder" 
                    :mode="'packlist'"
                    :currentOrder="currentOrder"
                    :currentOrderId="currentOrderId"
                    :openItems="openItems"
                    :packedItems="packedItems"
                    :pausedItems="pausedItems"
                    :productsCount="currentProducts.length"
                    :hasMissingItems="hasMissingItems"
                    :isMulticart="isMulticart"
                    :isWarranty="isWarranty"
                    :currentPrinter="currentPrinter"
                    :currentPackageWeight="currentPackageWeight"
                    :user="user"
                    @item-click="handlePacklistItemClick"
                    @open-printer-modal="openModal('settings')"
                    @open-problem-modal="openModal('problem')"
                    @open-reset-dialog="openBulkResetDialog"
                />
                
                <!-- Right Side - Articles Area -->
                <div class="packlist-articles-section">
                    <OrderBody
                        :articles="currentProducts"
                        :packlistItems="currentOrder?.packlistItems || []"
                        :upsellItems="upsellItems"
                        :currentFlyer="currentFlyer"
                        :isMulticart="isMulticart"
                        :hasMissingItems="hasMissingItems"
                        :isDesktop="true"
                        :isWarranty="isWarranty"
                        :warrantyReason="warrantyReason"
                        :productsCount="currentProducts.length"
                        :currentOrderId="currentOrderId"
                        :isDarkBackground="false"
                        :mode="'packlist'"
                        :nextItemToScan="nextItemToScan"
                        :isPartOfPacklistChecker="isPartOfPacklist"
                        :isInDifferentPacklistChecker="isInDifferentPacklist"
                        :layout="articleLayout"
                        @reset-article="resetArticle"
                        @open-warranty-modal="openModal('warranty')"
                        @toggle-background-mode="handleBackgroundToggle"
                    />
                </div>
            </div>
            
            <!-- Bottom Actions -->
            <div class="packlist-actions-section">
                <OrderActions
                    :mode="'packlist'"
                    :hasMissingItems="hasMissingItems"
                    :orderIsMulticart="isMulticart"
                    :scannerEnabled="scannerEnabled"
                    :multicartItemsMissingShelf="multicartItemsMissingShelf"
                    :multicartItemsHaveShelves="multicartItemsHaveShelves"
                    :multicartItemsInPacklistNeedShelf="multicartItemsInPacklistNeedShelf"
                    :currentPrinter="currentPrinter"
                    :currentPacklistName="this.$route.params.id"
                    :currentOrder="currentOrder"
                    :labelPrinted="labelPrinted"
                    :isPrinting="isPrinting"
                    :isPrintable="workflow.canPrint"
                    :isPrinted="isPrinted"
                    :currentPackstats="{ count: currentPackstats.count, time: currentPackstats.time }"
                    :needScan="needScan"
                    :scannableItems="scannableItems"
                    :allScannableItemsScanned="allScannableItemsScanned"
                    :nextItemToScan="nextItemToScan"
                    :scannedItemsCount="scannedItemsCount"
                    :totalItemsToScan="totalItemsToScan"
                    :isScanning="isScanning"
                    :isDesktop="isDesktop"
                    :isDevelopmentMode="isDevelopmentMode"
                    :tracking="currentOrder ? currentOrder.tracking : null"
                    :trackingIdScanned="trackingIdScanned"
                    :scannedTrackingId="scannedTrackingId"
                    @open-multicart-modal="openModal('multicart')"
                    @open-settings-modal="openModal('settings')"
                    @print="printLabel"
                    @pack="handlePack"
                    @handle-fullscreen="handleFullscreen"
                />
            </div>
        </div>

        <!-- SHARED MODALS -->
        <ProblemModal
            v-if="isProblemModalOpen"
            :currentItem="currentOrder"
            :user="user"
            :currentPacklistItemUserLaser="currentPacklistItemUserLaser"
            :showEngravingSection="true"
            @close-modal="closeModal"
            @reportProblem="handleProblemReport"
        />
        
        <WarrantyModal
            v-if="isWarrantyModalOpen"
            :warranty-reason="warrantyReason"
            :warranty-note="warrantyNote"
            :current-order="currentOrder"
            @close-modal="closeModal"
        />
        
        <!-- PACKLIST-SPECIFIC MODALS -->
        <MulticartConfirmModal
            v-if="isMulticartConfirmModalOpen"
            :multicart-payload="multicartPayload"
            :current-packlist-items="packlistItems"
            :current-packlist-name="packlistName"
            :current-order="currentOrder"
            :current-order-id="currentOrderId"
            :current-printer="currentPrinter"
            @close-modal="closeModal"
            @confirm-storage="handleMulticartStorage"
            @scanning-completed="handleScanningCompleted"
        />

        <DamagedDialog 
            :show="isDamagedDialogOpen"
            :article="selectedArticleForReset"
            :orderId="currentOrder ? currentOrder.orderId : null"
            :resetOnly="damageResetOnly"
            :orderProducts="resettableProducts"
            @close="closeDamagedDialog"
            @reset-confirmed="handleArticleReset"
        />

        <SettingsModal
            :show="isSettingsModalOpen"
            :currentPrinter="currentPrinter"
            :openPrinterTab="openPrinterTab"
            :user="user"
            @close-modal="closeSettingsModal"
            @select-printer="selectPrinter"
            @layout-changed="handleLayoutChanged"
            @scanner-mode-changed="handleScannerModeChanged"
        />

        <!-- Multicart Scanner Disabled Error Dialog -->
        <v-dialog v-model="showScannerDisabledError" max-width="500">
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
                    Scanner deaktiviert
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="text-center mb-4">
                        <v-icon color="warning" size="64">mdi-barcode-off</v-icon>
                    </div>
                    <p class="text-h6 text-center mb-3">Multicart-Bestellung kann nicht gedruckt werden</p>
                    <p class="text-body-1 text-center grey--text">
                        Diese Bestellung muss über das Warenkorbregal gebucht werden.
                        Aktivieren Sie den Scanner-Modus in den Einstellungen, um diese Bestellung direkt zu scannen und zu verpacken.
                    </p>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-btn text @click="showScannerDisabledError = false">
                        Schließen
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="openSettingsToScannerMode">
                        <v-icon left>mdi-cog</v-icon>
                        Einstellungen öffnen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <ScanErrorModal
            v-if="scanErrorModal.show"
            :show="scanErrorModal.show"
            :errorType="scanErrorModal.errorType"
            :errorContext="scanErrorModal.errorContext"
            @close="closeScanErrorModal"
            @continue-scanning="handleContinueScanning"
            @reset-current-item="handleResetCurrentItem"
            @report-problem="handleReportScanProblem"
        />

        <!-- Snackbar for notifications -->
        <v-snackbar
            v-model="snackbar.show"
            :color="snackbar.color"
            :timeout="snackbar.timeout"
            top
            right
        >
            {{ snackbar.message }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    v-bind="attrs"
                    @click="snackbar.show = false"
                >
                    Schließen
                </v-btn>
            </template>
        </v-snackbar>

        <!-- Loading State - Debug -->
        <div v-if="isLoadingPacklist || isLoadingItem" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: rgba(255,255,255,0.9);">
            <LoadingState 
                :message="isLoadingPacklist ? 'Packliste wird geladen...' : 'Lädt Bestellung...'"
                :subtitle="isLoadingPacklist ? 'Lade Packlisten-Elemente' : 'Lade Bestellungsdetails'"
            />
        </div>
        
        <!-- Empty State -->  
        <EmptyState 
            v-else-if="!currentOrder && !isLoadingPacklist && !isLoadingItem" 
            message="Keine Packlisten-Elemente verfügbar"
            @retry="getPacklist"
        />
    </div>
</template>

<script>
import OrderHeader from '../shared/OrderHeader.vue'
import OrderBody from '../shared/OrderBody.vue'
import OrderActions from '../shared/OrderActions.vue'
import LoadingState from '../shared/LoadingState.vue'
import EmptyState from '../shared/EmptyState.vue'
import BackButton from '../shared/BackButton.vue'
import DamagedDialog from '../shared/modals/DamagedDialog.vue'
import SettingsModal from '../shared/modals/SettingsModal.vue'
import WarrantyModal from '../shared/modals/WarrantyModal.vue'
import ProblemModal from '../shared/modals/ProblemModal.vue'
import ScanErrorModal from '../shared/modals/ScanErrorModal.vue'
import MulticartConfirmModal from './modals/MulticartConfirmModal.vue'

//Services
import PacklistService from '@/services/PacklistService.js';
// import PackagingService from '@/services/PackagingService.js'; // REMOVED: no longer used
import AuthService from '@/services/AuthService.js';
import EventBus, { PackTracker } from "@/event-bus";

//Static Data
import flyerMappingData from '../shared/data/flyerMapping.js';

// Scanner Mixin
import scannerInputMixin from '@/mixins/scannerInputMixin';
import '@/styles/scanner-input.css';

export default {
    name: 'PacklistRoot',
    mixins: [scannerInputMixin],
    components: {
        OrderHeader,
        OrderBody,
        OrderActions,
        LoadingState,
        EmptyState,
        BackButton,
        DamagedDialog,
        SettingsModal,
        WarrantyModal,
        ProblemModal,
        ScanErrorModal,
        MulticartConfirmModal
    },
    data() {
        return {
            packlistItems: [],
            currentPacklistItemIndex: 0,
            currentOrderId: null,
            currentPacklistItemId: null,
            currentOrder: null,
            currentPacklistItem: null,
            currentPacklistItemUserLaser: null,
            currentPackstats: {
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null,
                packEnd: null
            },
            currentModal: 'none',
            currentArticleToReset: null,
            currentPrinter: (() => {
                try {
                    const storedPrinter = localStorage.getItem('currentPrinter');
                    return storedPrinter ? JSON.parse(storedPrinter) : null;
                } catch (error) {
                    console.warn('Failed to parse stored printer data:', error);
                    return null;
                }
            })(),
            multicartPayload: null,
            user: null,
            isLoadingItem: false,
            isLoadingPacklist: true,
            isPrinting: false,
            labelPrinted: false,
            printButtonPressed: false,
            under8cm: false,
            articleLayout: 'linear',
            scannerEnabled: (() => {
                try {
                    const savedScannerMode = localStorage.getItem('multicartScannerEnabled');
                    return savedScannerMode === 'true';
                } catch (error) {
                    console.warn('Failed to load scanner mode from localStorage:', error);
                    return false;
                }
            })(),
            showScannerDisabledError: false,
            openPrinterTab: false,
            packlistName: null,
            itemViewStartTime: null, // Time when item was first loaded/viewed
            packingStarted: false,   // Whether actual packing has started
            
            // Damaged Dialog related
            isDamagedDialogOpen: false,
            selectedArticleForReset: null,
            damageResetOnly: true,
            
            // Settings Modal related
            isSettingsModalOpen: false,
            
            // Modal states for direct modal management
            isWarrantyModalOpen: false,
            isProblemModalOpen: false,
            isMulticartModalOpen: false,
            isMulticartConfirmModalOpen: false,
            
            // Scanning related
            isScanning: false,
            tabletScannerTimeout: null, // Timeout for tablet scanner debouncing
            scannerOptions: {
                enabled: true,
                refocusDelay: 3000,
                debug: false
            },
            scanErrorModal: {
                show: false,
                errorType: '',
                errorContext: {}
            },

            // Development mode
            isDevelopmentMode: false,

            // Tracking ID Scanning
            trackingIdScanned: false,
            scannedTrackingId: null,

            // Snackbar for notifications
            snackbar: {
                show: false,
                message: '',
                color: 'success',
                timeout: 3000
            },

            // Packlist Alerts (from Socket.io)
            currentAlert: null,
            alertDialog: false,

            upsellWhitelist: [
                "LYTO_TYPE_C_KABEL",
                "LYTO_NETZSTECKER",
                "LYTO_LED_SOCKEL_WHITE",
                "wooden_led_base_HV",
                "wooden_led_base_RGB",
                "NETZSTECKER",
                "TYP_C_USB_KABEL",
                "gravurfarbe_kinderbesteck",
                "PRIO_ORDER",
                "LYTO_SAMTBEUTEL_SCHWARZ",
                "LS24_SAMTBEUTEL_RED",
                "LS24_SAMTBEUTEL_SILVER",
                "LS24_SAMTBEUTEL_BLACK",
                "Samtbeutel (schwarz)",
                "Samtbeutel (rot)",
                "GF_Organzasäckchen",
                "GF_Samtsäckchen",
                "hauck_holzleiste_5mm",
                "upsell_holzsockel5mm",
                "samtbeutel_kordelzug",
                "stoffbeutel",
                "shopify_extra_helle_gravur",
                "Taschenmesserkette",
                "fruehstuecksbrett_tiere_upsell"
            ],
            cutleryPacklist: ['wmf', 'amefa'],
            // REMOVED: Performance optimization - carton display removed
            // currentCarton: null,
            flyerMapping: flyerMappingData
        }
    },
    computed: {
        messageSocket() {
            return this.$store.state.messages?.socket;
        },
        isWarranty() {
            return this.currentPacklistItem?.warranty;
        },
        warrantyReason() {
            return this.currentPacklistItem?.warrantyReason;
        },
        warrantyNote() {
            return this.currentPacklistItem?.warrantyNote;
        },
        openItems() {
            return this.packlistItems ? this.packlistItems.filter(item => item.status === 'open' || item.status === 'paused') : [];
        },
        pausedItems() {
            return this.packlistItems ? this.packlistItems.filter(item => item.status === 'paused') : [];
        },
        packedItems() {
            return this.packlistItems ? this.packlistItems
            .filter(item => item.status === 'packed')
            .sort((a, b) => {
                // If both have packEnd dates, compare them
                if (a.packEnd && b.packEnd) {
                    const dateA = new Date(a.packEnd);
                    const dateB = new Date(b.packEnd);
                    return dateB - dateA; // Newest first
                }
                
                // If a has packEnd but b doesn't, a comes after
                if (a.packEnd && !b.packEnd) {
                return -1;
                }
                
                // If b has packEnd but a doesn't, b comes after
                if (!a.packEnd && b.packEnd) {
                return 1;
                }
                
                // If neither has packEnd, maintain original order
                return 0;
            }) : [];
        },
        upsellItems() {
            if (!this.currentOrder?.products) return [];
            
            //Filter products that are upsell=true AND !isEvent OR in upsellWhitelist
            const filteredUpsellItems = this.currentOrder.products.filter(
                product => (product.upsell === true && !product.isEvent) || this.upsellWhitelist.includes(product.sku)
            );

            //Deduplicate products
            const deduplicatedProducts = filteredUpsellItems.reduce((acc, product) => {
                const existingProduct = acc.find(p => p.sku === product.sku);
                
                if (existingProduct) {
                    existingProduct.qty = (existingProduct.qty || 1) + (product.qty || 1);
                } else {
                    acc.push({
                        ...product,
                        qty: product.qty || 1
                    });
                }
                
                return acc;
            }, []);

            return deduplicatedProducts;
        },
        currentProducts() {
            if (!this.currentOrder?.products) return [];
            
            let dontShow = ["customization"];

            const filtered = this.currentOrder.products.filter(product => {
                const isUpsell = Boolean(product.upsell) === true;
                const isEvent = Boolean(product.isEvent) === true;
                const shouldInclude = (!isUpsell && !isEvent) || (isUpsell && isEvent);
                const notInUpsellWhitelist = !this.upsellWhitelist.includes(product.sku);
                const notDontShow = !dontShow.includes(product.sku);

                return shouldInclude && notInUpsellWhitelist && notDontShow;
            });

            return filtered;
        },
        currentFlyer() {
            try {
                if (!this.currentOrder) {
                    return [];
                }

                const orderId = this.currentOrder.orderId;
                const reference = this.currentOrder.reference;
                const flyers = [];

                if (!reference && orderId) {
                    if (orderId.toString().startsWith('MM')) {
                        const value = this.flyerMapping.find(f => f.value === 'MM');
                        if (value) {
                            flyers.push(value);
                        }
                    } else if (isNaN(orderId) && !orderId.toString().startsWith('#')) {
                        const value = this.flyerMapping.find(f => f.value === 'GF');
                        if (value) {
                            flyers.push(value);
                        }
                    } else {
                        console.log('❌ No orderId pattern match');
                    }
                }

                // If external order -> check for reference
                if (reference) {
                    if (reference.toString().startsWith('#BP')) {
                        const value = this.flyerMapping.find(f => f.value === '#BP');
                        if (value) {
                            flyers.push(value);
                        }
                    } else if (reference.toString().startsWith('#FW')) {
                        const value = this.flyerMapping.find(f => f.value === '#FW');
                        if (value) {
                            flyers.push(value);
                        }
                    } else {
                        console.warn('No reference pattern match');
                    }
                }

                // Check for extra flyer conditions based on product tables
                const acrylTables = ['printcut', 'acrylglas_stundenplan', 'acrylglas_nachtlicht', 'acrylanhaenger', 'florithings_shapes', 'lyto_nachtlicht', 'lyto_printcut_heart'];
                const toniTables = ['toni_regal', 'lyto_toni_regal'];
                const moneyboxTables = ['lyto_spardose', 'spardose', 'uv_spardose', 'shirtracer_spardose'];

                // Check if any product has a translated.table in acryl or toni tables
                const hasAcrylTable = this.currentProducts.some(product => {
                    const table = product.translated?.table;
                    if (table && acrylTables.includes(table)) {
                        return true;
                    }
                    return false;
                });

                const hasToniTable = this.currentProducts.some(product => {
                    const table = product.translated?.table;
                    if (table && toniTables.includes(table)) {
                        return true;
                    }
                    return false;
                });

                const hasMoneyBoxTable = this.currentProducts.some(product => {
                    const table = product.translated?.table;
                    if (table && moneyboxTables.includes(table)) {
                        return true;
                    }
                    return false;
                });

                // Add ACRYL flyer if any product has acryl table
                if (hasAcrylTable) {
                    const acrylFlyer = this.flyerMapping.find(f => f.value === 'ACRYL');
                    if (acrylFlyer && !flyers.some(f => f.value === 'ACRYL')) {
                        flyers.push(acrylFlyer);
                    }
                }

                // Add TONI flyer if any product has toni table
                if (hasToniTable) {
                    const toniFlyer = this.flyerMapping.find(f => f.value === 'TONI');
                    if (toniFlyer && !flyers.some(f => f.value === 'TONI')) {
                        flyers.push(toniFlyer);
                    }
                }

                if (hasMoneyBoxTable) {
                    const moneyboxFlyer = this.flyerMapping.find(f => f.value === 'SPARDOSE');
                    if (moneyboxFlyer && !flyers.some(f => f.value === 'SPARDOSE')) {
                        flyers.push(moneyboxFlyer);
                    }
                }

                return flyers;
            } catch (error) {
                console.error('❌ ERROR in currentFlyer computed property:', error);
                console.error('Error stack:', error.stack);
                return [];
            }
        },
        isMulticart() {
            // Return false if currentOrder or products are not loaded yet
            if (!this.currentOrder?.products || this.currentProducts.length === 0) {
                return false;
            }

            // Check if any product has isMultiCart property set to true
            // EXCLUDE whitelisted upsell items (they should not be treated as multicart items)
            return this.currentProducts.some(product =>
                product.isMultiCart === true && !this.upsellWhitelist.includes(product.sku)
            );
        },
        multicartItemsMissingShelf() {
            // Check if any multicart items that are NOT in current ORDER's packlist items are missing shelf assignments
            if (!this.currentOrder?.products || !this.packlistItems || this.packlistItems.length === 0) {
                return false;
            }

            //Check if the current packlist is a cutlery packlist
            const isCutleryPacklist = this.cutleryPacklist.some(prefix => this.packlistName.toLowerCase().includes(prefix));

            let multicartProducts = [];
            if (isCutleryPacklist) {
                // Filter multicart products, EXCLUDING whitelisted upsell items (they're not real multicart items) AND LYTO_BRETTCHEN
                multicartProducts = this.currentOrder.products.filter(p =>
                    p.isMultiCart === true && !this.upsellWhitelist.includes(p.sku) && p.sku !== 'LYTO_BRETTCHEN'
                );
            } else {
                // Filter multicart products, EXCLUDING whitelisted upsell items (they're not real multicart items)
                multicartProducts = this.currentOrder.products.filter(p =>
                    p.isMultiCart === true && !this.upsellWhitelist.includes(p.sku)
                );
            }
            if (multicartProducts.length === 0) {
                return false;
            }

            // Get packlist items for CURRENT ORDER from order data (includes all packlists/tables)
            const currentOrderPacklistItems = this.currentOrder.packlistItems || [];

            // Filter to only products NOT in current ORDER's packlist items
            // These are products that should already be on a shelf from previous packlists
            const productsNotInPacklist = multicartProducts.filter(product => {
                // Get designId from either location (root level or nested in attributes)
                const productDesignId = product.designId || product.attributes?.designId;

                const inPacklist = currentOrderPacklistItems.some(packlistItem => {
                    // Priority 1: Match by designId (most specific) if both have designIds
                    if (productDesignId && packlistItem.designId) {
                        return packlistItem.designId === productDesignId;
                    }
                    // Priority 2: Fall back to SKU matching (for products without designId)
                    return packlistItem.sku === product.sku;
                });
                return !inPacklist;
            });

            // If there are no products outside the current order's packlist, everything is fine
            if (productsNotInPacklist.length === 0) {
                return false;
            }

            // Return true if ANY product (not in order's packlist and not whitelisted) is missing a shelf
            const hasMissingShelf = productsNotInPacklist.some(p => !p.shelf || !p.shelf.slot);

            return hasMissingShelf;
        },
        multicartItemsInPacklistNeedShelf() {
            // IMPORTANT: Items IN the current packlist don't need shelf assignments or scanning
            // Only items NOT in packlist need shelves (to make them scannable)
            // This computed property should always return false
            return false;
        },
        multicartItemsHaveShelves() {
            // Check if any multicart items are in a DIFFERENT packlist (not the current one)
            // If so, they need to be handled via the multicart widget
            if (!this.currentOrder?.products || !this.currentOrder?.packlistItems) {
                return false;
            }

            // Filter multicart products, EXCLUDING whitelisted upsell items
            const multicartProducts = this.currentOrder.products.filter(p =>
                p.isMultiCart === true && !this.upsellWhitelist.includes(p.sku)
            );
            if (multicartProducts.length === 0) {
                return false;
            }

            // Get packlist items for CURRENT ORDER from order data (includes all packlists/tables)
            const currentOrderPacklistItems = this.currentOrder.packlistItems || [];

            // Check if ANY multicart product is in a different packlist
            const hasItemsInDifferentPacklist = multicartProducts.some(product => {
                // Get designId from product
                const productDesignId = product.designId || product.attributes?.designId;

                // Find matching packlist item
                const packlistItem = currentOrderPacklistItems.find(item => {
                    // Priority 1: Match by designId (most specific) if both have designIds
                    if (productDesignId && item.designId) {
                        return item.designId === productDesignId;
                    }
                    // Priority 2: Fall back to SKU matching
                    return item.sku === product.sku;
                });

                // If found in packlistItems, check if it's in a different packlist
                if (packlistItem && packlistItem.table) {
                    const isDifferentPacklist = packlistItem.table.toLowerCase() !== this.packlistName.toLowerCase();
                    return isDifferentPacklist;
                }

                return false;
            });

            return hasItemsInDifferentPacklist;
        },
        hasMissingItems() {
            if (!this.currentOrder || !this.currentOrder.products) {
                return false;
            }
            return this.currentOrder.products.some(x => x.status === 'missing');
        },
        isPrinted() {
            return this.printButtonPressed;
        },
        workflow() {
            // Guard clause: Don't enable anything if order isn't fully loaded
            if (!this.currentOrder || !this.currentOrder.products || this.currentOrder.products.length === 0) {
                return {
                    needScan: false,
                    canPrint: false,  // ✅ Disable print button until order loads
                    canPack: false
                };
            }

            const needScan = this.needScan;
            const allScannableItemsScanned = needScan ? this.allScannableItemsScanned : true;

            // Check if multicart workflow should block printing
            // Items IN packlist can always be printed (they're part of the packlist workflow)
            // Items NOT in packlist need shelf assignment OR scanner mode
            let requiresMulticartModal = false;

            if (this.isMulticart) {
                if (!this.scannerEnabled && this.multicartItemsMissingShelf) {
                    // Scanner OFF + items NOT in packlist missing shelves: Require multicart modal
                    // Note: If all items are IN packlist, multicartItemsMissingShelf = false, so we don't block
                    requiresMulticartModal = true;
                } else if (this.scannerEnabled && !needScan && this.multicartItemsMissingShelf) {
                    // Scanner ON + items NOT in packlist missing shelves + no scanning needed: Block
                    requiresMulticartModal = true;
                }
                // else: All items are in packlist OR have shelves → Don't block printing
            }

            // Determine if we can print:
            // - Must have printer
            // - If needScan is true, all scannable items must be scanned
            // - Must not require multicart modal
            const canPrint = this.currentPrinter && allScannableItemsScanned && !requiresMulticartModal;

            return {
                needScan: needScan,
                canPrint: canPrint,
                canPack: this.labelPrinted && this.currentOrder && !this.isPrinting
            };
        },
        needScan() {
            // Check if this is a multicart order that requires scanning
            if (!this.currentOrder?.products || this.currentOrder.products.length === 0) {
                return false;
            }

            const products = this.currentOrder.products;
            // Filter multicart products, EXCLUDING whitelisted upsell items (they're not real multicart items)
            const multicartProducts = products.filter(product => {
                const isWhitelisted = this.upsellWhitelist.includes(product.sku);
                return product.isMultiCart === true && !isWhitelisted;
            });
            const isMulticartOrder = multicartProducts.length > 0;

            // If not a multicart order, no scanning needed
            if (!isMulticartOrder) {
                return false;
            }

            // If scanner is disabled, no scanning workflow
            if (!this.scannerEnabled) {
                return false;
            }

            // For scanning to be enabled, we need to check if there are items NOT in packlist
            // that need shelf assignments (these are the ones that need scanning)
            const productsNotInPacklist = multicartProducts.filter(product => {
                const inPacklist = this.isPartOfPacklist(product.sku);
                return !inPacklist;
            });

            // If no products outside packlist (excluding whitelisted), no scanning needed
            if (productsNotInPacklist.length === 0) {
                return false;
            }

            // Check if ANY products NOT in packlist (and not whitelisted) have shelf assignments
            // We need scanning if at least one item has a shelf (those items need to be scanned)
            const someNonPacklistItemsHaveShelves = productsNotInPacklist.some(product =>
                product.shelf && product.shelf.slot
            );

            // Enable scanning if:
            // - It's a multicart order AND
            // - Scanner mode is enabled AND
            // - There are items not in packlist AND
            // - At least one of those items has a shelf assignment
            const needScan = someNonPacklistItemsHaveShelves;
            return needScan;
        },
        scannableItems() {
            if (!this.currentOrder?.products || !this.needScan) {
                return [];
            }
            
            const allProducts = this.currentOrder.products;

            // ✅ CORRECT LOGIC: Items NOT in packlist + have shelf + ready + multicart (excluding whitelisted) → need scanning
            const scannable = allProducts.filter(product => {
                const isPartOfPacklist = this.isPartOfPacklist(product.sku);
                const notInPacklist = !isPartOfPacklist;  // ✅ Items NOT in packlist
                const hasShelf = Boolean(product.shelf?.slot);
                const isReady = product.status === 'ready';
                const isMulticart = product.isMultiCart === true;
                const isUpsellWhitelisted = this.upsellWhitelist.includes(product.sku);

                // Whitelisted upsell items should never be scannable, even if they have isMultiCart flag
                const shouldScan = notInPacklist && hasShelf && isReady && isMulticart && !isUpsellWhitelisted;
                return shouldScan;
            });
            
            return scannable;
        },
        allScannableItemsScanned() {
            if (!this.needScan || this.scannableItems.length === 0) {
                return true;
            }
            
            // Check if all scannable items are scanned
            return this.scannableItems.every(item => {
                const scannedCount = item.scannedCount || 0;
                const requiredQty = item.qty || 1;
                return item.scanned === true && scannedCount >= requiredQty;
            });
        },
        nextItemToScan() {
            const needScan = this.needScan;
            const scannableItems = this.scannableItems;
            
            if (!needScan || !scannableItems.length) {
                return null;
            }
            
            // Find the first unscanned item from scannable items
            const unscannedItem = scannableItems.find(item => {
                const scannedCount = item.scannedCount || 0;
                const requiredQty = item.qty || 1;
                return scannedCount < requiredQty;
            });
            
            if (unscannedItem) {
                return {
                    sku: unscannedItem.sku,
                    name: unscannedItem.attributes?.Wunschname || unscannedItem.translated?.sku || unscannedItem.sku,
                    qty: unscannedItem.qty || 1,
                    scannedCount: unscannedItem.scannedCount || 0,
                    slot: unscannedItem.shelf?.slot
                };
            }
            
            return null;
        },
        scannedItemsCount() {
            if (!this.needScan || !this.scannableItems.length) return 0;
            
            // Count total scanned items from scannable items
            return this.scannableItems.reduce((total, item) => total + (item.scannedCount || 0), 0);
        },
        totalItemsToScan() {
            if (!this.needScan || !this.scannableItems.length) return 0;
            
            // Count total items that need scanning
            return this.scannableItems.reduce((total, item) => total + (item.qty || 1), 0);
        },
        isCurrentItemPacked() {
            // Check if the current item is already packed
            if (!this.currentPacklistItemId || !this.packlistItems) return false;
            const currentItem = this.packlistItems.find(item => item.id === this.currentPacklistItemId);
            return currentItem?.status === 'packed';
        },
        
        resettableProducts() {
            // Return only products that are part of the packlist items
            // Filter currentProducts to only show items that exist in packlistItems
            if (!this.currentProducts || !this.packlistItems) {
                return [];
            }

            return this.currentProducts.filter(product =>
                this.isPartOfPacklist(product.sku)
            );
        }
    },
    methods: {
        // Socket.io Alert Handler
        handlePacklistAlert(alert) {
            this.currentAlert = alert;
            this.alertDialog = true;
        },
        onAlertDismissed() {
            this.alertDialog = false;
            this.currentAlert = null;
        },

        //Helper methods
        isPartOfPacklist(sku) {
            // Check if this SKU is found in the current packlist items
            if (!this.packlistItems || !sku) {
                return false;
            }
            
            const foundInPacklist = this.packlistItems.some(packlistItem => 
                packlistItem.sku === sku
            );
            
            return foundInPacklist;
        },

        isInDifferentPacklist(product) {
            // Check if this product exists in a different packlist (not the current one)
            // Uses order.packlistItems which has the table name
            if (!this.currentOrder?.packlistItems || !product) {
                return false;
            }

            // Get designId from product
            const productDesignId = product.designId || product.attributes?.designId;

            // Find matching packlist item by designId or SKU
            const packlistItem = this.currentOrder.packlistItems.find(item => {
                if (productDesignId && item.designId) {
                    return item.designId === productDesignId;
                }
                return item.sku === product.sku;
            });

            if (!packlistItem) {
                return false; // Not in any packlist
            }

            // Check if the packlist item's table name differs from current packlist name
            const isDifferentPacklist = packlistItem.table &&
                                       packlistItem.table.toLowerCase() !== this.packlistName.toLowerCase();

            return isDifferentPacklist;
        },

        //Data functions and handlers
        async getPacklist() {
            const packlistName = this.$route.params.id;

            if (!packlistName) {
                return;
            }

            try {
                const response = await PacklistService.getPacklistItems(packlistName);

                if (response.status === 200 && response.data.length > 0) {
                    if (this.packlistItems) {
                        this.packlistItems.splice(0, this.packlistItems.length, ...response.data);
                    } else {
                        this.$set(this, 'packlistItems', response.data);
                    }
                } else if (response.status === 200 && response.data.length === 0) {
                    this.$router.push('/');
                }
            } catch (error) {
                console.error(error);
            }
        },
        async getCurrentPacklistItem(orderId, id) {
            try {
                const response = await PacklistService.getPacklistItem(orderId);

                if (response.status === 200) {
                    this.currentOrder = response.data;
                    this.currentPacklistItem = response.data;
                    this.currentPacklistItemUserLaser = response.data.userLaser;
                    if (id) {
                        this.currentPacklistItemId = id;

                    }
                } 
            } catch (error) {
                console.error('Error getting current packlist item:', error);
            }
        },
        // REMOVED: Performance optimization - getPackagingSelection was causing sync issues when switching packlist items
        // async getPackagingSelection() {
        //     try {
        //         if (!this.currentOrderId) {
        //             console.warn('📦 No current order ID available for packaging selection');
        //             return;
        //         }

        //         const response = await PackagingService.getPackagingSelection(this.currentOrderId);
        //
        //         if (response.status === 200 && response.data) {
        //             if (response.data.success && response.data.selectedCarton) {
        //                 this.currentCarton = response.data.selectedCarton;
        //             } else {
        //                 console.warn('❌ Packaging selection failed:', response.data.error || 'Unknown error');
        //                 this.currentCarton = null;
        //             }
        //         } else {
        //             console.warn('❌ Invalid packaging selection response');
        //             this.currentCarton = null;
        //         }
        //     } catch (error) {
        //         console.error('❌ Error getting packaging selection:', error);
        //         this.currentCarton = null;
        //     }
        // },
        // Pack tracking now handled by EventBus system
        async loadPackStats() {
            try {
                const response = await PacklistService.getPackStats();
                if (response?.data) {
                    this.currentPackstats.count = response.data.count;
                    this.currentPackstats.time = response.data.time;
                }
            } catch (error) {
                console.error('Error loading pack stats:', error);
            }
        },
        async handlePackstats() {
            // Store the view start time and start tracking
            const currentPackSession = localStorage.getItem('lastTimestamp');

            if (currentPackSession && this.user?.id === JSON.parse(currentPackSession).userId) {
                const packSession = JSON.parse(currentPackSession);
                this.itemViewStartTime = new Date(packSession.packStart);
                this.currentPackstats.packStart = packSession.packStart;
            } else {
                const viewStartTime = new Date();
                this.itemViewStartTime = viewStartTime;

                const newPackSession = {
                    userId: this.user?.id,
                    packStart: viewStartTime
                };

                this.currentPackstats.packStart = newPackSession.packStart;
                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));
            }

            // Reset packing started flag for new item
            this.packingStarted = false;
            await this.startActualPacking();
        },

        async startActualPacking() {
            // This is called when item loads to start tracking
            if (!this.packingStarted && this.itemViewStartTime && this.currentPacklistItemId) {
                await PackTracker.startPackTracking(this.currentPacklistItemId, this.itemViewStartTime);
                this.packingStarted = true;
            }
        },
        async printLabel(under8cm = false) {
            if (!this.currentPrinter) {
                this.openPrinterTab = true;
                this.openModal('settings');
                return;
            }

            if (this.isPrinting) {
                return;
            }

            this.isPrinting = true;

            try {
                await PacklistService.print(this.currentOrderId, this.currentPrinter.url, under8cm);
                this.labelPrinted = true;
                this.printButtonPressed = true;

                // Force Vue to update the UI to ensure tracking widget appears
                this.$nextTick(() => {
                    this.$forceUpdate();
                });
            } catch (error) {
                console.error('Error printing label:', error);
            } finally {
                this.isPrinting = false;
            }
        },
        async pack() {
            try {
                await PackTracker.endPackTracking(this.currentPacklistItemId);

                // Store the current index before refreshing
                const currentOrderId = this.currentOrderId;
                const currentIndex = this.packlistItems ? this.packlistItems.findIndex(item => 
                    item.id === this.currentPacklistItemId
                ) : -1;

                // Set loading before refreshing data
                this.isLoadingItem = true;

                // Refresh the packlist
                await this.getPacklist();

                // Reset print button (like old version)
                this.labelPrinted = false;
                this.printButtonPressed = false;

                // Find the next open item after our current position
                let nextItem = null;
                
                // First try to find open items with the same order ID (for multi-item orders)
                const sameOrderOpenItems = this.packlistItems ? this.packlistItems.filter(item => 
                    item && item.status === 'open' && 
                    item.orderId === currentOrderId &&
                    item.id !== this.currentPacklistItemId
                ) : [];
                
                if (sameOrderOpenItems.length > 0) {
                    // If there are still open items from the same order, pick the first one
                    nextItem = sameOrderOpenItems[0];
                } else {
                    // Otherwise, find the next open item in the sequence
                    const nextOpenItems = this.packlistItems ? this.packlistItems
                        .filter(item => item && item.status === 'open')
                        .filter(item => {
                            const itemIndex = this.packlistItems.indexOf(item);
                            return itemIndex > currentIndex;
                        }) : [];
                        
                    if (nextOpenItems.length > 0) {
                        nextItem = nextOpenItems[0];
                    } else if (this.openItems && this.openItems.length > 0) {
                        // If no next items found, use the first open item as fallback
                        nextItem = this.openItems[0];
                    }
                }
                
                if (nextItem) {
                    // Switch to next order using unified method
                    await this.switchToOrder(nextItem.orderId, nextItem.id);
                    
                    // Update the index to match the new current item
                    this.currentPacklistItemIndex = this.openItems ? this.openItems.findIndex(item => item && item.id === nextItem.id) : -1;
                }

                // Update pack stats for next item (but don't start tracking yet)
                const newPackSession = {
                    userId: this.user ? this.user.id : null,
                    packStart: new Date()
                };
                this.currentPackstats.packStart = newPackSession.packStart;
                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));

                // Store view start time for next item but don't track yet
                this.itemViewStartTime = newPackSession.packStart;
                this.packingStarted = false; // Reset for next item

                // Finally turn off loading
                this.isLoadingItem = false;

            } catch (error) {
                console.error('Error packing item:', error);
                this.isLoadingItem = false;
            }
        },
        async handleMulticartConfirm() {
            if (!this.currentPrinter) {
                this.openPrinterTab = true;
                this.openModal('settings');
                return;
            }

            try {
                // Request slot WITHOUT items - backend won't print barcode labels yet
                const response = await PacklistService.requestMultiCartSlot(
                    this.currentOrderId,
                    {
                        printer: this.currentPrinter.url,
                        wklPrinter: this.currentPrinter.wkl,
                        labelCount: 1,
                        items: [] // Empty - no barcode labels yet
                    }
                );

                if (response.status === 200) {
                    this.multicartPayload = {
                        ...response.data,
                        printer: this.currentPrinter.url
                    };
                    this.isMulticartConfirmModalOpen = true;
                }
            } catch (error) {
                console.error('❌ Error requesting multicart slot:', error);
            }
        },
        async handleMulticartStorage(payload) {
            try {
                try {
                    const response = await PacklistService.putMultiCartItems(
                        this.currentOrderId,
                        payload
                    );

                    if (response.status === 200) {
                        this.closeModal();

                        // Store current order ID before refresh
                        const currentOrderId = this.currentOrderId;

                        // Refresh the packlist data
                        await this.getPacklist();

                        // Find the next item to navigate to (skip items from same order)
                        const nextItem = this.openItems.find(item => item.orderId !== currentOrderId);

                        if (nextItem) {
                            // Switch to next order using unified method
                            await this.switchToOrder(nextItem.orderId, nextItem.id);

                            // Update the index after order switch
                            this.currentPacklistItemIndex = this.openItems.findIndex(item => item.id === nextItem.id);
                        } else if (this.openItems.length > 0) {
                            // Fallback: switch to first available order
                            const fallbackItem = this.openItems[0];
                            await this.switchToOrder(fallbackItem.orderId, fallbackItem.id);
                            this.currentPacklistItemIndex = 0;
                        } else {
                            this.$router.push('/');
                        }
                    }
                } catch (error) {
                    console.error('Error putting multicart slot:', error);
                }
            } catch (error) {
                console.error('Error handling multicart storage:', error);
            }
        },

        async handleScanningCompleted() {
            try {
                // Store the current item ID and order ID BEFORE refreshing
                const currentOrderId = this.currentOrderId;
                const currentItemId = this.currentPacklistItemId;

                // Find current index in the OLD array before refresh
                const oldIndex = this.packlistItems ? this.packlistItems.findIndex(item =>
                    item.id === currentItemId
                ) : -1;

                // Set loading before refreshing data
                this.isLoadingItem = true;

                // Refresh the packlist data (this updates this.packlistItems)
                await this.getPacklist();

                // Reset print button
                this.labelPrinted = false;
                this.printButtonPressed = false;

                // After refresh, find where the current item is NOW in the NEW array
                // (it might have changed status to 'shelf' or 'packed')
                const newIndex = this.packlistItems ? this.packlistItems.findIndex(item =>
                    item.id === currentItemId
                ) : -1;

                // Find the next open item after our current position
                let nextItem = null;

                // First try to find open items with the same order ID (for multi-item orders)
                const sameOrderOpenItems = this.packlistItems ? this.packlistItems.filter(item =>
                    item && item.status === 'open' &&
                    item.orderId === currentOrderId &&
                    item.id !== currentItemId
                ) : [];

                if (sameOrderOpenItems.length > 0) {
                    // If there are still open items from the same order, pick the first one
                    console.log('✅ Found', sameOrderOpenItems.length, 'open items from same order');
                    nextItem = sameOrderOpenItems[0];
                } else {
                    // Otherwise, find the next open item in the sequence after current position
                    // Use the NEW index after refresh
                    const nextOpenItems = this.packlistItems ? this.packlistItems
                        .filter(item => item && item.status === 'open')
                        .filter(item => {
                            const itemIndex = this.packlistItems.indexOf(item);
                            return itemIndex > newIndex;
                        }) : [];

                    console.log('🔍 Found', nextOpenItems.length, 'open items after index', newIndex);

                    if (nextOpenItems.length > 0) {
                        nextItem = nextOpenItems[0];
                    } else if (this.openItems && this.openItems.length > 0) {
                        // If no next items found after current index, use the first open item as fallback
                        console.log('⚠️ No items after current index, using first open item as fallback');
                        nextItem = this.openItems[0];
                    }
                }

                if (nextItem) {                    
                    // Switch to next item using unified method
                    await this.switchToOrder(nextItem.orderId, nextItem.id);

                    // Update the index to match the new current item
                    this.currentPacklistItemIndex = this.openItems ? this.openItems.findIndex(item => item && item.id === nextItem.id) : -1;
                } else {
                    this.$router.push('/');
                }

                // Update pack stats for next item (but don't start tracking yet)
                const newPackSession = {
                    userId: this.user ? this.user.id : null,
                    packStart: new Date()
                };
                this.currentPackstats.packStart = newPackSession.packStart;
                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));

                // Store view start time for next item but don't track yet
                this.itemViewStartTime = newPackSession.packStart;
                this.packingStarted = false; // Reset for next item

                // Finally turn off loading
                this.isLoadingItem = false;

            } catch (error) {
                console.error('Error handling scanning completion:', error);
                this.isLoadingItem = false;
            }
        },

        //UI Functions and Handlers
        resetItemSpecificState() {
            // Reset all item-specific state when switching items
            this.labelPrinted = false;
            this.isPrinting = false;
            this.printButtonPressed = false;
            this.currentModal = 'none';
            this.currentArticleToReset = null;
            this.multicartPayload = null;
            this.packingStarted = false;  // Reset packing state for new item
            this.itemViewStartTime = null; // Will be set on next item load
            // this.currentCarton = null; // REMOVED: carton display removed

            // Reset scanning state for new item
            this.isScanning = false;
            this.scanErrorModal.show = false;

            // Reset tracking ID state
            this.trackingIdScanned = false;
            this.scannedTrackingId = null;
        },
        
        // Unified method for switching orders - ensures proper loading sequence
        async switchToOrder(orderId, packlistItemId) {
            // Reset all item-specific state when switching items (CRITICAL for tracking widget!)
            this.resetItemSpecificState();

            // Update IDs (this will trigger currentOrderId watcher)
            this.currentOrderId = orderId;
            this.currentPacklistItemId = packlistItemId;
            this.currentPacklistItem = null; // Clear for reactivity

            // Wait a tick for watcher to process the change
            await this.$nextTick();
        },
        next() {
            const currentOrderId = this.currentOrderId;
            let nextIndex = this.currentPacklistItemIndex + 1;
            
            // Find the next item with a different order ID
            while (nextIndex < this.openItems.length && this.openItems[nextIndex]?.orderId === currentOrderId) {
                nextIndex++;
            }
            
            // If we found a valid next item with different order ID, use it
            if (nextIndex < this.openItems.length) {
                this.currentPacklistItemIndex = nextIndex;
            }
        },
        prev() {
            const currentOrderId = this.currentOrderId;
            let prevIndex = this.currentPacklistItemIndex - 1;
            
            // Find the previous item with a different order ID
            while (prevIndex >= 0 && this.openItems[prevIndex]?.orderId === currentOrderId) {
                prevIndex--;
            }
            
            // If we found a valid previous item with different order ID, use it
            if (prevIndex >= 0) {
                this.currentPacklistItemIndex = prevIndex;
            }
        },
        handleKeydown(event) {
            // Guard against undefined event
            if (!event || !event.key) {
                console.warn('handleKeydown called with invalid event:', event);
                return;
            }

            // Arrow keys work always
            if (event.key === 'ArrowRight') {
                this.next();
                return;
            } else if (event.key === 'ArrowLeft') {
                this.prev();
                return;
            }

            // Other keyboard shortcuts (b, c, a) only work when scanner mode is OFF
            if (this.scannerEnabled) {
                return;
            }

            if (event.key === 'b') {
                // Check if this is a multicart order that requires multicart modal
                if (this.isMulticart && this.multicartItemsMissingShelf) {
                    console.warn('🚫 Cannot print: Multicart order requires multicart booking');
                    this.showScannerDisabledError = true;
                    return;
                }
                if (this.workflow.canPrint) {
                    this.printLabel();
                }
            } else if (event.key === 'c' && this.workflow.canPack) {
                this.pack();
            } else if (event.key === 'a') {
                this.under8cm = !this.under8cm;
                localStorage.setItem('under8cm', JSON.stringify(this.under8cm));
            }
        },
        handlePacklistItemClick(item) {
            //Add loading overlay
            this.isLoadingItem = true;
            
            // Reset all item-specific state when switching items
            this.resetItemSpecificState();

            this.currentOrderId = item.orderId;
            this.currentPacklistItemId = item.id;
            this.currentPacklistItem = item;
        },

        // Scanner Methods (via scannerInputMixin)
        /**
         * Override scanner keydown to allow foot keyboard shortcuts when scanner mode is OFF
         */
        handleScannerKeydownOverride(event) {
            // If scanner mode is OFF, allow keyboard shortcuts (b, c, a, arrows)
            // by preventing the scanner input from capturing them
            if (!this.scannerEnabled) {
                const shortcutKeys = ['b', 'c', 'a', 'ArrowLeft', 'ArrowRight'];
                if (shortcutKeys.includes(event.key)) {
                    event.preventDefault();
                    event.stopPropagation();

                    // Blur the scanner input so Android doesn't show keyboard
                    if (event.target) {
                        event.target.blur();
                    }

                    // Directly call our keyboard handler
                    this.handleKeydown(event);
                    return;
                }
            }

            // Otherwise, use the mixin's scanner handling
            this.handleScannerKeydown(event);
        },

        /**
         * Handle tablet scanner input (for camera/built-in scanners)
         * Uses debouncing to wait for complete scan before processing
         */
        handleTabletScannerInput(event) {
            const value = event.target.value;

            // Clear any existing timeout
            if (this.tabletScannerTimeout) {
                clearTimeout(this.tabletScannerTimeout);
            }

            // Wait for input to stabilize (scanner finished sending all characters)
            this.tabletScannerTimeout = setTimeout(() => {
                const finalValue = event.target.value.trim();

                if (finalValue) {
                    // Process the complete scan
                    this.onScanReceived(finalValue);

                    // Clear the input for next scan
                    event.target.value = '';
                }
            }, 500); // Wait 500ms for scan to complete
        },

        /**
         * Handle scan from DataWedge (called by scannerInputMixin)
         * This method is called when a complete scan is received from TC200 or TC22
         */
        onScanReceived(scannedValue) {
            // IMPORTANT: Don't handle scans when MulticartConfirmModal is open
            // The modal has its own scan input field
            if (this.isMulticartConfirmModalOpen) {
                return;
            }

            // Prevent actions during printing/processing
            if (this.isPrinting) {
                return;
            }

            if (this.scannerEnabled && this.labelPrinted && !this.trackingIdScanned && this.isTrackingIdScan(scannedValue)) {
                this.handleTrackingIdScan(scannedValue);
                return;
            }

            // Only handle article scanning if order requires scanning
            if (!this.needScan) {
                return;
            }

            // Set scanning state
            this.isScanning = true;

            // Parse and handle the scanned data
            const parsedData = this.parseScanData(scannedValue);

            if (parsedData) {
                this.handleScannedData(parsedData);
            } else {
                console.error('❌ Invalid barcode format:', scannedValue);
                this.showScanError('wrong-article', {
                    scannedSku: scannedValue,
                    expectedSku: 'Valid barcode format'
                });
            }

            // Reset scanning state
            this.isScanning = false;
        },

        parseScanData(scanString) {
            // Parse format for US keyboard: MM12345|box_teein|A1|1234567890
            const parts = scanString.split('|');

            if (parts.length >= 4) {
                return {
                    orderId: parts[0],
                    sku: parts[1],
                    location: parts[2],
                    hashcode: parts[3]
                };
            }

            // Fallback: try apostrophe delimiter for German keyboard scans
            // German keyboard (QWERTZ): MM12345'box?teein'A1'1234567890
            // German keyboard swaps Y/Z and converts _ to ? and | to '
            const altParts = scanString.split("'");
            if (altParts.length >= 4) {
                // Convert German keyboard characters back to original:
                // ? → _ (underscore)
                // z → y, Z → Y (QWERTZ to QWERTY)
                const sku = altParts[1]
                    .replace(/\?/g, '_')  // Replace ? with _
                    .replace(/z/g, 'y')   // Replace lowercase z with y
                    .replace(/Z/g, 'Y');  // Replace uppercase Z with Y

                return {
                    orderId: altParts[0],
                    sku: sku,
                    location: altParts[2],
                    hashcode: altParts[3]
                };
            }

            // NEW: Support orderID-only scanning for transition period
            // Check if this looks like an orderID (e.g., MM12345, #MM12345, 465446)
            // OrderIDs can be:
            // - Letter prefix + numbers (MM12345, GF12345): 1-3 letters, 4-8 numbers
            // - Pure numbers (465446): 5-8 digits
            // - With optional # prefix (#MM12345)
            const orderIdPattern = /^#?(?:[A-Z]{1,3}\d{4,8}|\d{5,8})$/i;
            if (orderIdPattern.test(scanString)) {
                return {
                    orderId: scanString.replace('#', ''), // Remove # if present
                    orderIdOnly: true // Flag to indicate this is orderID-only
                };
            }

            console.warn('❌ [parseScanData] No match found for scan string:', scanString);
            return null;
        },

        async handleScannedData(scannedData) {
            let matchingArticle = null;

            // TRANSITION PERIOD: Handle orderID-only scans
            if (scannedData.orderIdOnly) {
                // Verify the orderID matches the current order
                const currentOrderId = String(this.currentOrder?.orderId || '').replace('#', '');
                const scannedOrderId = String(scannedData.orderId || '').replace('#', '');

                if (currentOrderId !== scannedOrderId) {
                    console.error('❌ [OrderID-Only] Scanned orderID does not match current order');
                    this.showScanError('wrong-article', {
                        scannedSku: `Order ${scannedData.orderId}`,
                        expectedSku: `Order ${this.currentOrder?.orderId}`
                    });
                    return;
                }

                // Find the next article that hasn't been fully scanned yet
                matchingArticle = this.scannableItems.find(product => {
                    const scannedCount = product.scannedCount || 0;
                    const requiredQty = product.qty || 1;
                    const unscanned = scannedCount < requiredQty;
                    console.log(`📋   Checking ${product.sku} (${product.designId}): scanned=${scannedCount}/${requiredQty}, unscanned=${unscanned}`);
                    return unscanned;
                });

                if (!matchingArticle) {
                    this.showScanError('quantity-exceeded', {
                        itemSku: 'All items',
                        scannedCount: this.scannedItemsCount,
                        requiredQty: this.totalItemsToScan
                    });
                    return;
                }
            } else {
                // Standard scan with full barcode (orderId|sku|slot|hashcode)
                // IMPORTANT: Match by combination of orderID, SKU, slot, and hashcode
                // This ensures we find the exact instance when there are multiple items with the same SKU
                // Try exact match: orderID + SKU + slot + hashcode + not fully scanned
                if (scannedData.hashcode && scannedData.location) {
                    matchingArticle = this.scannableItems.find(product => {
                        const productOrderId = String(product.orderId || product.translated?.orderId || '');
                        const scannedOrderId = String(scannedData.orderId || '');

                        const skuMatch = product.sku === scannedData.sku;
                        const orderIdMatch = productOrderId === scannedOrderId ||
                            productOrderId.replace('#', '') === scannedOrderId.replace('#', '');
                        const slotMatch = product.shelf?.slot === scannedData.location;
                        const hashcodeMatch = product.shelfHashcode &&
                            String(product.shelfHashcode) === String(scannedData.hashcode);

                        // Also check if this item hasn't been scanned yet
                        const currentScannedCount = product.scannedCount || 0;
                        const requiredQty = product.qty || 1;
                        const notFullyScanned = currentScannedCount < requiredQty;

                        return skuMatch && orderIdMatch && slotMatch && hashcodeMatch && notFullyScanned;
                    });
                }

                // Fallback 1: Match by orderID + SKU + hashcode only (no slot check)
                if (!matchingArticle && scannedData.hashcode) {
                    matchingArticle = this.scannableItems.find(product => {
                        const productOrderId = String(product.orderId || product.translated?.orderId || '');
                        const scannedOrderId = String(scannedData.orderId || '');

                        const skuMatch = product.sku === scannedData.sku;
                        const orderIdMatch = productOrderId === scannedOrderId ||
                            productOrderId.replace('#', '') === scannedOrderId.replace('#', '');
                        const hashcodeMatch = product.shelfHashcode &&
                            String(product.shelfHashcode) === String(scannedData.hashcode);

                        const currentScannedCount = product.scannedCount || 0;
                        const requiredQty = product.qty || 1;
                        const notFullyScanned = currentScannedCount < requiredQty;

                        return skuMatch && orderIdMatch && hashcodeMatch && notFullyScanned;
                    });
                }

                // Fallback 2: Match by orderID + SKU only (find any unscanned instance)
                if (!matchingArticle) {
                    matchingArticle = this.scannableItems.find(product => {
                        const productOrderId = String(product.orderId || product.translated?.orderId || '');
                        const scannedOrderId = String(scannedData.orderId || '');

                        const skuMatch = product.sku === scannedData.sku;
                        const orderIdMatch = productOrderId === scannedOrderId ||
                            productOrderId.replace('#', '') === scannedOrderId.replace('#', '');

                        const currentScannedCount = product.scannedCount || 0;
                        const requiredQty = product.qty || 1;
                        const notFullyScanned = currentScannedCount < requiredQty;

                        return skuMatch && orderIdMatch && notFullyScanned;
                    });
                }

                if (!matchingArticle) {
                    console.error('❌ [QR Scan] No matching article found or all instances already scanned');
                    this.showScanError('wrong-article', {
                        scannedSku: scannedData.sku,
                        expectedSku: this.scannableItems.map(p => p.sku).join(', ')
                    });
                    return;
                }
            }

            // Process the scan for this article
            await this.scanArticle(matchingArticle, scannedData);
        },

        async scanArticle(article, scannedData = null) {
            const currentScannedCount = article.scannedCount || 0;
            const requiredQty = article.qty || 1;

            // Check if article is already fully scanned
            if (currentScannedCount >= requiredQty) {
                this.showScanError('quantity-exceeded', {
                    itemSku: article.sku,
                    scannedCount: currentScannedCount,
                    requiredQty: requiredQty
                });
                return;
            }

            // Find and update the article in the original currentOrder.products array
            // Use designId if available, otherwise fall back to object reference matching
            let originalProductIndex = -1;

            if (article.designId) {
                // If designId exists and is not undefined, use it for matching
                originalProductIndex = this.currentOrder.products.findIndex(p =>
                    p.designId === article.designId && p.sku === article.sku
                );
            } else {
                // If no designId, find by object reference (same instance)
                // This ensures we update the exact product object we found in scannableItems
                originalProductIndex = this.currentOrder.products.findIndex(p => p === article);
            }

            if (originalProductIndex !== -1) {
                const newScannedCount = currentScannedCount + 1;

                // Update scanned count
                this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', newScannedCount);
                this.$set(this.currentOrder.products[originalProductIndex], 'lastScanTime', new Date().toISOString());

                // Mark as fully scanned if all quantity is scanned
                if (newScannedCount >= requiredQty) {
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', true);
                }

                // Check if all items are scanned and auto-print
                if (this.allScannableItemsScanned && !this.isPrinted && this.currentPrinter) {
                    await this.printLabel();
                }
            } else {
                console.error('❌ Could not find product in currentOrder.products array!', {
                    sku: article.sku,
                    designId: article.designId,
                    article
                });
            }
        },

        // Scan Error Modal handlers
        showScanError(errorType, errorContext = {}) {
            this.scanErrorModal = {
                show: true,
                errorType: errorType,
                errorContext: errorContext
            };
        },
        
        closeScanErrorModal() {
            this.scanErrorModal.show = false;
        },
        
        handleContinueScanning() {
            console.log('▶️ Continue scanning after error');
            // Just continue - user can scan next item
        },
        
        handleResetCurrentItem() {
            console.log('🔄 Reset current item after error');
            // Reset functionality can be added if needed
        },
        
        handleReportScanProblem(problemData) {
            console.log('🐛 Scan problem reported:', problemData);
            // Could integrate with problem reporting system
        },
        handleFullscreen() {
            try {
                if (!document.fullscreenElement) {
                    this.isFullscreen = true;
                    // Try different fullscreen methods for better compatibility
                    if (document.documentElement.requestFullscreen) {
                        document.documentElement.requestFullscreen();
                    } else if (document.documentElement.mozRequestFullScreen) {
                        document.documentElement.mozRequestFullScreen();
                    } else if (document.documentElement.webkitRequestFullscreen) {
                        document.documentElement.webkitRequestFullscreen();
                    } else if (document.documentElement.msRequestFullscreen) {
                        document.documentElement.msRequestFullscreen();
                    } else {
                        console.warn('Fullscreen API not supported on this device');
                        this.isFullscreen = false;
                    }
                } else {
                    this.isFullscreen = false;
                    if (document.exitFullscreen) {
                        document.exitFullscreen();
                    } else if (document.mozCancelFullScreen) {
                        document.mozCancelFullScreen();
                    } else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen();
                    } else if (document.msExitFullscreen) {
                        document.msExitFullscreen();
                    }
                }
            } catch (error) {
                console.warn('Fullscreen operation failed:', error);
                this.isFullscreen = false;
            }
        },
        openModal(modalId) {
            switch(modalId) {
                case 'settings':
                    this.isSettingsModalOpen = true;
                    break;
                case 'warranty':
                    this.isWarrantyModalOpen = true;
                    break;
                case 'problem':
                    this.isProblemModalOpen = true;
                    break;
                case 'multicart':
                    // Skip the simple confirmation modal and go directly to item selection
                    this.handleMulticartConfirm();
                    break;
                case 'multicart-confirm':
                    this.isMulticartConfirmModalOpen = true;
                    break;
                default:
                    this.currentModal = modalId;
            }
        },
        closeModal() {
            this.currentModal = 'none';
            this.isWarrantyModalOpen = false;
            this.isProblemModalOpen = false;
            this.isMulticartModalOpen = false;
            this.isMulticartConfirmModalOpen = false;
        },
        closeSettingsModal() {
            this.isSettingsModalOpen = false;
            this.openPrinterTab = false;
        },
        resetArticle(article) {
            // Open the shared DamagedDialog directly instead of using EventBus
            this.selectedArticleForReset = article;
            this.damageResetOnly = true; // Default to reset only mode
            this.isDamagedDialogOpen = true;
        },
        
        openBulkResetDialog() {
            // Open reset dialog without a specific article (bulk reset mode)
            this.selectedArticleForReset = null; // No specific article
            this.damageResetOnly = true; // Default to reset only mode
            this.isDamagedDialogOpen = true;
        },
        selectPrinter(printer) {
            this.currentPrinter = printer;
            localStorage.setItem('currentPrinter', JSON.stringify(printer));
            this.openPrinterTab = false; // Reset the flag after selection
            this.isSettingsModalOpen = false; // Close settings modal
        },
        handleLayoutChanged(newLayout) {
            this.articleLayout = newLayout;
        },
        handleScannerModeChanged(enabled) {
            this.scannerEnabled = enabled;

            // IMPORTANT: Keep mixin's scannerOptions.enabled in sync
            this.scannerOptions.enabled = enabled;

            // Initialize or cleanup scanner input when mode changes
            this.$nextTick(() => {
                if (enabled && this.$refs.scannerInput) {
                    this.initScannerInput();
                } else if (!enabled) {
                    this.cleanupScannerInput();
                }
            });
        },
        openSettingsToScannerMode() {
            this.showScannerDisabledError = false;
            this.isSettingsModalOpen = true;
            // TODO: We could add a prop to SettingsModal to auto-open the scanner tab
        },

        // Tracking ID scanning methods
        isTrackingIdScan(scanString) {
            // Exclude pipe-delimited barcodes (article scans)
            if (scanString.includes("|") || scanString.includes("'")) {
                return false;
            }

            // Check if it's alphanumeric (letters and numbers only, no special chars)
            const isAlphanumeric = /^[A-Z0-9]+$/i.test(scanString);

            // Minimum length: 10 characters to support various tracking ID formats
            // Examples:
            // - DHL: 00340435068500145361 (20 chars)
            // - Hermes: CI836610632DE (13 chars)
            // - Other carriers may have different lengths
            const minLength = 10;
            const result = isAlphanumeric && scanString.length >= minLength;

            return result;
        },

        async handleTrackingIdScan(trackingId) {
            // Check if we're in the right state for tracking ID scanning
            if (!this.labelPrinted) {
                console.warn('⚠️ [PacklistRoot] Label not printed yet');
                this.showSnackbar('❌ Bitte zuerst Label drucken', 'warning');
                return;
            }

            // Check if already scanned
            if (this.trackingIdScanned) {
                console.warn('⚠️ [PacklistRoot] Tracking ID already scanned');
                this.showSnackbar('📋 Tracking ID bereits gescannt', 'info');
                return;
            }

            try {
                // Verify tracking ID matches the expected one (if we have an expected value)
                const expectedTracking = this.currentOrder?.tracking;

                if (expectedTracking && trackingId !== expectedTracking) {
                    console.warn('⚠️ [PacklistRoot] Scanned tracking ID does not match expected:', {
                        scanned: trackingId,
                        expected: expectedTracking
                    });
                    this.showSnackbar('❌ Falsche Tracking ID gescannt', 'error');
                    return;
                }

                // Mark as scanned
                this.scannedTrackingId = trackingId;
                this.trackingIdScanned = true;

                this.showSnackbar(`✅ Tracking ID gescannt - Verpacke...`, 'success');

                // Automatically pack and proceed to next order
                await this.pack();

            } catch (error) {
                console.error('❌ [PacklistRoot] Error handling tracking ID scan:', error);
                this.showSnackbar('❌ Fehler beim Scannen der Tracking ID', 'error');
            }
        },

        handlePack() {
            this.pack();
        },

        showSnackbar(message, color = 'success', timeout = 3000) {
            this.snackbar = {
                show: true,
                message,
                color,
                timeout
            };
        },

        // Problem reporting functions (like old PacklistRoot)
        async handleProblemReport(note) {
            if (!note) return;
            
            try {
                const response = await PacklistService.sendFeedback(this.currentOrderId, note);
                
                if (response.status === 200) {
                    this.closeModal();

                    //Refresh packlist to get updated order data
                    await this.handleRefreshPacklist();
                } else {
                    console.error('Failed to report problem:', response);
                }
            } catch (error) {
                console.error('Error reporting problem:', error);
            }
        },
        
        async handleRefreshPacklist() {
            try {
                await this.getPacklist();
            } catch (error) {
                console.error('Error refreshing packlist:', error);
            }
        },
        
        handleBackgroundToggle() {
            // Handle background toggle from OrderBody
        },
        
        // Damaged Dialog handlers
        closeDamagedDialog() {
            this.isDamagedDialogOpen = false;
            this.selectedArticleForReset = null;
            this.damageResetOnly = true;
        },

        async handleArticleReset(resetData) {
            try {
                // Check if the API call was successful
                if (resetData.success) {
                    const selectedArticles = resetData.selectedArticles; // Array of SKUs
                    const totalItems = resetData.totalItems;
                    const resetOnly = resetData.resetOnly;

                    console.log(`✅ ${totalItems} article(s) ${resetOnly ? 'reset' : 'marked as damaged'} successfully:`, selectedArticles.join(', '));

                    // Close the dialog
                    this.closeDamagedDialog();

                    // Refresh the packlist to get updated order data using the same logic as handleRefreshPacklist
                    await this.handleRefreshPacklist();

                    console.log('🔄 Packlist refreshed after article reset');
                } else {
                    console.error('❌ Article reset failed');
                }

            } catch (error) {
                console.error('❌ Error handling article reset:', error);
            }
        },

        // Dev Mode Methods
        async devToggleArticleScanned(article) {
            if (!this.isDevelopmentMode) return;
            
            const currentScannedCount = article.scannedCount || 0;
            const requiredQty = article.qty || 1;
            
            // Find the article in currentOrder.products
            const originalProductIndex = this.currentOrder.products.findIndex(p => 
                p.designId === article.designId && p.sku === article.sku
            );

            if (originalProductIndex !== -1) {
                if (currentScannedCount >= requiredQty) {
                    // Unscan it
                    this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', 0);
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', false);
                } else {
                    // Scan to full
                    this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', requiredQty);
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', true);
                    this.$set(this.currentOrder.products[originalProductIndex], 'lastScanTime', new Date().toISOString());
                }
                
                // Check if all items are scanned and auto-print
                await this.$nextTick(); // Wait for reactive updates
                if (this.allScannableItemsScanned && !this.isPrinted && this.currentPrinter) {
                    await this.printLabel();
                }
            }
        },

        async devScanAllArticles() {
            if (!this.isDevelopmentMode || !this.needScan || !this.scannableItems) return;
            
            this.scannableItems.forEach((article) => {
                const originalProductIndex = this.currentOrder.products.findIndex(p => 
                    p.designId === article.designId && p.sku === article.sku
                );
                
                if (originalProductIndex !== -1) {
                    const requiredQty = article.qty || 1;
                    this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', requiredQty);
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', true);
                    this.$set(this.currentOrder.products[originalProductIndex], 'lastScanTime', new Date().toISOString());
                }
            });
            
            // Check if all items are scanned and auto-print
            await this.$nextTick(); // Wait for reactive updates
            if (this.allScannableItemsScanned && !this.isPrinted && this.currentPrinter) {
                await this.printLabel();
            }
        },

        devResetScans() {
            if (!this.isDevelopmentMode || !this.needScan || !this.scannableItems) return;
            
            this.scannableItems.forEach((article) => {
                const originalProductIndex = this.currentOrder.products.findIndex(p => 
                    p.designId === article.designId && p.sku === article.sku
                );
                
                if (originalProductIndex !== -1) {
                    this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', 0);
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', false);
                    this.$delete(this.currentOrder.products[originalProductIndex], 'lastScanTime');
                }
            });
        },

        toggleDevMode() {
            this.isDevelopmentMode = !this.isDevelopmentMode;
        }
    },
    watch: {
        messageSocket: {
            handler(socket) {
                if (socket) {
                    socket.on('packlist-alert', this.handlePacklistAlert);
                }
            },
            immediate: true
        },
        currentPacklistItemIndex: {
            handler(newVal, oldVal) {
                // Guard against invalid indices and undefined openItems
                if (!this.openItems || newVal === undefined || newVal === null || newVal < 0) {
                    // Silently return during initialization or when data is not ready
                    return;
                }

                // Check if index is within bounds
                // This can happen during transitions when items are packed/removed
                // Silently return - the index will be corrected by the next item logic
                if (newVal >= this.openItems.length) {
                    return;
                }
                
                // Show loading if this is not the initial load (oldVal exists)
                if (oldVal !== undefined) {
                    this.isLoadingItem = true;
                }
                
                const currentItem = this.openItems[newVal];
                if (currentItem) {
                    // Reset all item-specific state when switching items
                    this.resetItemSpecificState();
                    
                    this.currentOrderId = currentItem.orderId;
                    this.currentPacklistItemId = currentItem.id;
                    this.currentPacklistItem = currentItem;
                    
                    // Check if the new item has warranty and auto-open modal
                    if (currentItem.warranty) {
                        this.isWarrantyModalOpen = true;
                    }
                } else {
                    console.warn('currentPacklistItemIndex points to undefined item:', newVal);
                }
            },
            immediate: true
        },
        currentOrderId: {
            async handler(newVal) {
                if (newVal) {
                    // Load order data and packaging in parallel for immediate display
                    await Promise.all([
                        this.getCurrentPacklistItem(newVal, this.currentPacklistItemId),
                        //this.getPackagingSelection()
                    ]);
                }
            },
            deep: true
        },
        currentOrder: {
            async handler(newVal, oldVal) {
                // Skip if only scan properties changed (prevent infinite loop when scanning)
                if (oldVal && newVal && oldVal.orderId === newVal.orderId) {
                    return;
                }

                // Hide loading when order is successfully loaded
                if (newVal) {
                    this.isLoadingItem = false;

                    // Initialize pack tracking for the new item via handlePackstats
                    // Only if user is loaded (if not, mounted() hook will handle initial tracking)
                    if (this.user && this.currentPacklistItemId) {
                        await this.handlePackstats();
                    }

                    // Check for warranty and auto-open modal
                    if (newVal && newVal.warranty) {
                        this.isWarrantyModalOpen = true;
                    }

                    // Force workflow recomputation after order is fully loaded
                    this.$forceUpdate();
                }
            },
            deep: true
        },
        currentProducts: {
            handler(newVal) {
                if (newVal && Array.isArray(newVal)) {
                    console.log('currentProducts count:', newVal.length);
                } else {
                    console.warn('currentProducts is not a valid array:', newVal);
                }
            },
            immediate: true
        },
        upsellItems: {
            handler(newVal) {
                console.log('upsellItems watcher triggered:', newVal);
                if (newVal && Array.isArray(newVal)) {
                    console.log('upsellItems count:', newVal.length);
                } else {
                    console.warn('upsellItems is not a valid array:', newVal);
                }
            },
            immediate: true
        },
        currentPrinter: {
            handler(newVal) {
                if (newVal !== undefined && newVal !== null) {
                    localStorage.setItem('currentPrinter', JSON.stringify(newVal));
                }
            }
        }
    },
    async mounted() {
        // Listen for EventBus events (like damaged dialog)
        EventBus.$on("REFRESH_PACKLIST", async () => {
            try {
                const currentOrderId = this.currentOrderId;
                await this.getPacklist();
                
                // Check if current order still exists after refresh
                if (currentOrderId && this.packlistItems) {
                    const currentOrderStillExists = this.packlistItems.some(item => item.orderId === currentOrderId);
                    
                    if (!currentOrderStillExists && this.openItems.length > 0) {
                        // Reset state for new order
                        this.resetItemSpecificState();
                        
                        // Switch to first available order
                        await this.switchToOrder(this.openItems[0].orderId, this.openItems[0].id);
                        this.currentPacklistItemIndex = 0;
                    } else if (!currentOrderStillExists && this.openItems.length === 0) {
                        // No more open orders, redirect to main page
                        this.$router.push('/');
                    }
                }
            } catch (error) {
                console.error('❌ Error during packlist refresh:', error);
            }
        });

        if (this.isDesktopLayout) {
            // Load saved layout preference from localStorage
            const savedLayout = localStorage.getItem('articleLayout');
            this.articleLayout = savedLayout || 'linear';

            // For touch devices using desktop layout (tablets), try fullscreen
            const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            if (hasTouch && this.windowWidth <= 1366) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Only try fullscreen if we can detect fullscreen capability
                        if (document.fullscreenEnabled !== false) {
                            this.handleFullscreen();
                        } else {
                            console.warn('Fullscreen not available on this device');
                        }
                    }, 500); // Small delay to ensure page is fully loaded
                });
            }
        } else {
            this.articleLayout = 'linear';
        }

        try {
            // IMPORTANT: Sync mixin's scannerOptions.enabled with scannerEnabled from settings
            this.scannerOptions.enabled = this.scannerEnabled;

            // Global keydown handler in CAPTURE phase (runs before any input fields get the event)
            // ONLY active when scanner mode is OFF - otherwise scanner input needs to receive events
            this.globalKeydownHandler = (event) => {
                // Skip if scanner mode is ON - let scanner input handle everything
                if (this.scannerEnabled) {
                    return;
                }

                // Only intercept specific keys when scanner mode is OFF
                const interceptKeys = ['b', 'c', 'a', 'ArrowLeft', 'ArrowRight'];

                if (interceptKeys.includes(event.key)) {
                    // Prevent default browser behavior AND stop propagation
                    event.preventDefault();
                    event.stopPropagation();

                    // If any input is focused, blur it to prevent Android keyboard
                    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                        document.activeElement.blur();
                    }

                    // Now handle the key press
                    this.handleKeydown(event);
                }
            };

            // Add in CAPTURE phase (useCapture = true) to intercept before input fields
            window.addEventListener('keydown', this.globalKeydownHandler, true);

            // Set packlistName from route
            this.packlistName = this.$route.params.id;
            
            //Get Packlistitems on initial load
            await this.getPacklist();

            //Set currentOrderId to the first item in the packlist
            if (this.packlistItems.length > 0 && this.openItems.length > 0) {
                this.currentOrderId = this.openItems[0].orderId;
                this.currentPacklistItemId = this.openItems[0].id;
            } else if (this.openItems.length === 0) {
                // All items are packed, redirect to main page
                this.$router.push('/');
                return;
            }

            // Load pack stats (order data and packaging handled by currentOrderId watcher)
            await this.loadPackStats();

            // currentPrinter is now loaded in data initialization
            
            // Get user and initialize timer functionality
            this.user = (await AuthService.getUser()).data;
    
            // Pack tracking now handled by handlePackstats
            if (this.currentPacklistItemId && this.user) {
                await this.handlePackstats();
            } else {
                console.warn('📊 Cannot start initial pack tracking:', {
                    currentPacklistItemId: this.currentPacklistItemId,
                    user: !!this.user
                });
            }
            
            // Load saved article layout preference
            const savedLayout = localStorage.getItem('articleLayout');
            if (savedLayout) {
                this.articleLayout = savedLayout;
            }
            
            // Progress is now calculated in real-time in Timer component

            // Expose dev mode toggle to window
            window.toggleDevMode = this.toggleDevMode;
        } catch (error) {
            console.error('❌ Error during PacklistRoot initialization:', error);
        } finally {
            // Always hide initial loading overlay, even if there's an error
            this.isLoadingPacklist = false;
        }
    },
    beforeDestroy() {
        // Clean up Socket.io listeners
        if (this.messageSocket) {
            this.messageSocket.off('packlist-alert', this.handlePacklistAlert);
        }

        // Clean up tablet scanner timeout
        if (this.tabletScannerTimeout) {
            clearTimeout(this.tabletScannerTimeout);
        }

        // Clean up event listeners
        if (this.globalKeydownHandler) {
            window.removeEventListener('keydown', this.globalKeydownHandler, true);
        }

        // Clean up EventBus listeners
        EventBus.$off("REFRESH_PACKLIST");

        // Clean up global functions
        delete window.toggleDevMode;
    }
}
</script>

<style>
.packlist {
    box-sizing: border-box;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile browsers */
    min-height: 100vh; /* Fallback for older browsers */
    position: relative;
    overflow: hidden;
    width: 100vw;
    max-width: 100vw;
}


/* Desktop Layout (L-shaped) - FORCED FOR ALL DEVICES */
.packlist-desktop-layout {
    display: flex !important;
    flex-direction: column !important;
    height: 100dvh !important;
    width: 100% !important;
    overflow: hidden !important;
}

/* Top section with three columns */
.packlist-desktop-top {
    display: flex !important;
    flex: 1 !important;
    height: calc(100dvh - 120px) !important; /* Reserve space for actions at bottom */
    overflow: hidden !important;
}

/* Left Sidebar - Packlist Items */
.packlist-sidebar {
    height: 100% !important;
    background-color: #f9f9f9 !important;
    border-right: 1px solid #e0e0e0 !important;
    flex-shrink: 0 !important;
    overflow: hidden !important;
}

/* Middle Column - Header Content */
.packlist-header-column {
    width: clamp(280px, 25vw, 350px) !important; /* Responsive width with min/max constraints */
    height: 100% !important;
    background-color: #f9f9f9 !important;
    border-right: 1px solid #e0e0e0 !important;
    flex-shrink: 0 !important;
    overflow-y: auto !important;
    padding: 12px !important;
}

.packlist-header-content-desktop {
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    height: 100% !important;
    gap: 12px !important;
}

.header-top-section {
    display: flex !important;
    flex-direction: column !important;
    gap: 14px !important;
    flex: 1 !important;
}

.header-bottom-section {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
    flex-shrink: 0 !important;
    padding-top: 12px !important;
    border-top: 1px solid #e0e0e0 !important;
}

.packlist-header-customer-info-desktop {
    flex-shrink: 0 !important;
}

.customer-info-column {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
}

.order-id-section,
.customer-details-section {
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
}

.order-id-section-content {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    flex-wrap: wrap !important;
}

.info-label {
    font-size: 12px !important;
    font-weight: 600 !important;
    color: #666 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
}

.customer-details {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
}

.order-id-chip {
    width: fit-content !important;
    display: inline-flex !important;
}


.packlist-header-order-id {
    font-size: 1.2rem !important;
    font-weight: 600 !important;
}

.problem-button-header {
    margin-top: 6px !important;
    width: 100% !important;
}

.packlist-header-customer-name {
    font-size: 1.2rem !important;
    font-weight: 600 !important;
}

.packlist-header-customer-address {
    font-size: 1.2rem !important;
}

/* Articles Section - Right side in desktop */
.packlist-articles-section {
    flex: 1 !important;
    overflow: hidden !important;
    background-color: #fff !important;
    height: 100% !important;
}

/* Actions Section - Bottom spanning full width */
.packlist-actions-section {
    flex-shrink: 0 !important;
    background-color: #f0f0f0 !important;
    border-top: 1px solid #e0e0e0 !important;
    width: 100% !important;
}



/* Mobile Specific Adjustments */
@media (max-width: 767px) {
    .packlist-mobile {
        height: 100vh;
        height: 100dvh; /* Better mobile browser support */
        min-height: -webkit-fill-available; /* iOS Safari compatibility */
    }
    
    .packlist-actions-section {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        margin: 0;
        padding: 0;
        width: 100%;
    }
}






/* Loading Overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
}

.loading-content {
    background: white;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
    color: #333;
}

@keyframes spin {
    0% { 
        transform: rotate(0deg); 
    }
    100% { 
        transform: rotate(360deg); 
    }
}

/* Comprehensive viewport and compatibility fixes */
@supports (height: 100dvh) {
    .packlist,
    .packlist-mobile,
    .packlist-desktop-layout {
        height: 100dvh;
        min-height: 100dvh;
    }
}

/* iOS Safari specific fixes */
@supports (-webkit-appearance: none) {
    .packlist-mobile {
        min-height: -webkit-fill-available;
    }
}

/* Force proper positioning across all browsers */
.packlist-actions-section {
    box-sizing: border-box;
    margin: 0 !important;
    padding: 0 !important;
}


/* Dev Terminal Styles */
.dev-terminal {
    position: fixed;
    top: 70px;
    right: 16px;
    width: 400px;
    max-height: calc(100vh - 100px);
    z-index: 9999;
    background: #0d1117;
    border: 1px solid #00ff41;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(0, 255, 65, 0.3),
                0 0 40px rgba(0, 255, 65, 0.1);
    font-family: 'Courier New', monospace;
    overflow: hidden;
}

.terminal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: linear-gradient(180deg, #1a1f2e 0%, #0d1117 100%);
    border-bottom: 1px solid #00ff41;
}

.terminal-title {
    display: flex;
    align-items: center;
    color: #00ff41;
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 1px;
}

.terminal-blink {
    animation: blink 1s infinite;
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

.terminal-status {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #00ff41;
    font-size: 10px;
    letter-spacing: 1px;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #00ff41;
    box-shadow: 0 0 10px #00ff41;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
}

.terminal-body {
    padding: 12px;
    max-height: calc(100vh - 150px);
    overflow-y: auto;
    background: #0d1117;
}

.terminal-body::-webkit-scrollbar {
    width: 8px;
}

.terminal-body::-webkit-scrollbar-track {
    background: #1a1f2e;
}

.terminal-body::-webkit-scrollbar-thumb {
    background: #00ff41;
    border-radius: 4px;
}

.terminal-content {
    color: #00ff41;
}

.terminal-section {
    margin-bottom: 12px;
}

.terminal-prompt {
    color: #00ff41;
    font-size: 11px;
    font-weight: bold;
    margin-bottom: 8px;
    text-shadow: 0 0 5px rgba(0, 255, 65, 0.5);
}

.terminal-hint {
    color: #4a9960;
    font-size: 10px;
    font-style: italic;
    margin-top: 4px;
    margin-bottom: 8px;
}

.terminal-data {
    background: #1a1f2e;
    border: 1px solid #2d3748;
    border-left: 3px solid #00ff41;
    padding: 8px;
    border-radius: 4px;
    font-size: 10px;
    color: #00ff41;
    max-height: 150px;
    overflow: auto;
    margin: 0;
    line-height: 1.4;
    text-shadow: 0 0 2px rgba(0, 255, 65, 0.3);
}

.terminal-article-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.terminal-article-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    background: #1a1f2e;
    border: 1px solid #2d3748;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.terminal-article-item:hover {
    background: #2d3748;
    border-color: #00ff41;
}

.terminal-article-item.scanned {
    border-color: #00ff41;
    background: rgba(0, 255, 65, 0.1);
}

.article-sku {
    color: #00ff41;
    font-size: 11px;
    flex: 1;
}

.article-qty {
    color: #64ffda;
    font-size: 10px;
    margin-right: 8px;
}

.terminal-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.terminal-btn {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: bold;
    font-family: 'Courier New', monospace;
    letter-spacing: 0.5px;
    border: 1px solid;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-transform: uppercase;
}

.terminal-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.2);
}

.terminal-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.terminal-btn-primary {
    background: transparent;
    color: #00ff41;
    border-color: #00ff41;
    box-shadow: 0 0 10px rgba(0, 255, 65, 0.3);
}

.terminal-btn-primary:hover:not(:disabled) {
    background: rgba(0, 255, 65, 0.1);
    box-shadow: 0 0 15px rgba(0, 255, 65, 0.5);
}

.terminal-btn-secondary {
    background: transparent;
    color: #64ffda;
    border-color: #64ffda;
}

.terminal-btn-secondary:hover:not(:disabled) {
    background: rgba(100, 255, 218, 0.1);
}

.terminal-btn-success {
    background: transparent;
    color: #ffeb3b;
    border-color: #ffeb3b;
    box-shadow: 0 0 10px rgba(255, 235, 59, 0.3);
}

.terminal-btn-success:hover:not(:disabled) {
    background: rgba(255, 235, 59, 0.1);
    box-shadow: 0 0 15px rgba(255, 235, 59, 0.5);
}

</style>